import { useRef, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Loader2, Upload, X, Crop as CropIcon, Circle, Square } from "lucide-react";
import { api, img, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Field } from "./Fields";

/* Croppable photo uploader. Lets the admin pick a photo, drag/zoom to crop,
 * then uploads the exported WebP-friendly PNG blob to /admin/upload.
 *
 * Props:
 *  - value    : current photo URL (empty string = no photo).
 *  - onChange : (url: string) => void. Called with the uploaded URL (or "").
 *  - shape    : "round" | "rect". The admin can switch inside the modal.
 *  - onShapeChange : (shape: "round" | "rect") => void.
 *  - alt      : alt text sent along with the upload.
 *  - testid   : prefix for data-testid attributes.
 */

async function cropToBlob(imageSrc, pixels, mimeType = "image/jpeg", quality = 0.92) {
  const image = await new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, pixels.x, pixels.y, pixels.width, pixels.height, 0, 0, pixels.width, pixels.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), mimeType, quality));
}

export default function ImageCropUpload({ label = "Şablon fotoğrafı", value, onChange, shape = "round", onShapeChange, alt = "", testid = "tpl-photo", hint }) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState("");          // local object URL for cropping
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalShape, setModalShape] = useState(shape || "round");

  const aspect = modalShape === "rect" ? 16 / 10 : 1;
  const cropShape = modalShape === "rect" ? "rect" : "round";

  const pickFile = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("JPEG, PNG veya WebP seçin");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Dosya 10 MB'den büyük olamaz");
      return;
    }
    const url = URL.createObjectURL(f);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPx(null);
    setModalShape(shape || "round");
    setOpen(true);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_, px) => setAreaPx(px), []);

  const saveCrop = async () => {
    if (!src || !areaPx) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, areaPx, "image/jpeg", 0.92);
      if (!blob) throw new Error("Kırpma başarısız");
      const file = new File([blob], "crop.jpg", { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", alt || "");
      const { data } = await api.post("/admin/upload", fd);
      onChange?.(data.url);
      onShapeChange?.(modalShape);
      toast.success("Fotoğraf yüklendi");
      setOpen(false);
      URL.revokeObjectURL(src);
      setSrc("");
    } catch (e) {
      toast.error(errMsg(e) || "Yüklenemedi");
    }
    setBusy(false);
  };

  const closeModal = () => {
    if (busy) return;
    if (src) URL.revokeObjectURL(src);
    setSrc("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    toast.info("Fotoğraf kaldırıldı");
  };

  const previewRound = shape !== "rect";

  return (
    <Field label={label} hint={hint || "JPEG/PNG/WebP, en fazla 10 MB. Yüklerken yuvarlak (avatar) veya dikdörtgen olarak kırpabilirsiniz."}>
      <div className="flex items-center gap-3" data-testid={testid}>
        <div
          className={`w-20 h-20 border bg-stone-100 overflow-hidden flex items-center justify-center shrink-0 ${previewRound ? "rounded-full" : "rounded-lg"}`}
          data-testid={`${testid}-preview`}
        >
          {value ? (
            <img src={img(value)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <Upload size={18} className="text-stone-300" />
          )}
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onFile}
            data-testid={`${testid}-input`}
          />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={pickFile} data-testid={`${testid}-button`}>
              <Upload size={14} className="mr-1" />
              {value ? "Değiştir" : "Yükle"}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid={`${testid}-clear`}>
                <X size={14} className="mr-1" /> Kaldır
              </Button>
            )}
          </div>
          {value && (
            <div className="flex items-center gap-1 text-[11px] text-stone-500">
              <span>Şekil:</span>
              <button
                type="button"
                onClick={() => onShapeChange?.("round")}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition ${shape !== "rect" ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:border-stone-500"}`}
                data-testid={`${testid}-shape-round`}
              >
                <Circle size={10} />Yuvarlak
              </button>
              <button
                type="button"
                onClick={() => onShapeChange?.("rect")}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition ${shape === "rect" ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:border-stone-500"}`}
                data-testid={`${testid}-shape-rect`}
              >
                <Square size={10} />Dikdörtgen
              </button>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
          data-testid={`${testid}-modal`}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-stone-50">
              <div className="flex items-center gap-2">
                <CropIcon size={15} className="text-stone-500" />
                <p className="font-semibold text-sm">Fotoğrafı kırp</p>
              </div>
              <button type="button" onClick={closeModal} className="text-stone-500 hover:text-stone-900 p-1" data-testid={`${testid}-modal-close`}>
                <X size={16} />
              </button>
            </div>
            <div className="relative bg-stone-900 h-[360px]">
              {src && (
                <Cropper
                  image={src}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  cropShape={cropShape}
                  showGrid={cropShape === "rect"}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <div className="px-5 py-4 space-y-4 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-stone-500">Şekil</span>
                <button
                  type="button"
                  onClick={() => setModalShape("round")}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition ${modalShape === "round" ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:border-stone-500"}`}
                  data-testid={`${testid}-modal-shape-round`}
                >
                  <Circle size={11} />Yuvarlak (avatar)
                </button>
                <button
                  type="button"
                  onClick={() => setModalShape("rect")}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition ${modalShape === "rect" ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:border-stone-500"}`}
                  data-testid={`${testid}-modal-shape-rect`}
                >
                  <Square size={11} />Dikdörtgen (16:10)
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-stone-500 w-12 shrink-0">Yakınlaş</span>
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1"
                  data-testid={`${testid}-modal-zoom`}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={closeModal} disabled={busy} data-testid={`${testid}-modal-cancel`}>Vazgeç</Button>
                <Button type="button" onClick={saveCrop} disabled={busy || !areaPx} data-testid={`${testid}-modal-save`}>
                  {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CropIcon size={14} className="mr-1" />}
                  Kırp ve Kaydet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Field>
  );
}
